import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import axios from 'axios';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './react-datepicker-override.css';

import { Loading } from '../stories/Loading';
import PrimaryButton from './lib/buttons/PrimaryButton';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  datePicker: {
    height: '24px',
    '&>div>input::active': {
      border: 'none',
    },
    '&>div>input': {
      height: '24px',
    },
  },
  button: {
    maxWidth: '320px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
}));

interface ReportsProps {}

const Reports = (props: ReportsProps) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [reportDate, setReportDate] = useState<Date>();

  const generateCheckoutReport = () => {
    if (reportDate) {
      setLoading(true);
      const selectedDateStr = moment(reportDate).format('YYYY-MM-DD');

      axios
        .get(
          `/api/getConsolidatedReport?date=${selectedDateStr}&type=checkout`,
          { responseType: 'blob' }
        )
        .then((res) => res.data)
        .then((blob) => {
          var file = new Blob([blob], { type: 'application/pdf' });
          var url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `CheckoutReport-${selectedDateStr}.pdf`
          );
          document.body.appendChild(link);
          link.click();
          link?.parentNode?.removeChild(link);
        })
        .catch((err) => {
          console.log(err);
          alert('Error while downloading Checkout report!');
        })
        .finally(() => setLoading(false));
    }
  };

  const generateFoodReport = () => {
    if (reportDate) {
      setLoading(true);
      const selectedDateStr = moment(reportDate).format('YYYY-MM-DD');

      axios
        .get(`/api/getConsolidatedReport?date=${selectedDateStr}&type=food`, {
          responseType: 'blob',
        })
        .then((res) => res.data)
        .then((blob) => {
          var file = new Blob([blob], { type: 'application/pdf' });
          var url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Food-Report-${selectedDateStr}.pdf`);
          document.body.appendChild(link);
          link.click();
          link?.parentNode?.removeChild(link);
        })
        .catch((err) => {
          alert('Error while downloading Food report!');
          console.log(err);
        })
        .finally(() => setLoading(false));
    }
  };

  const generateLaundryReport = () => {
    if (reportDate) {
      setLoading(true);

      const selectedDateStr = moment(reportDate).format('YYYY-MM-DD');

      axios
        .get(`/api/getConsolidatedReport?date=${selectedDateStr}&type=misc`, {
          responseType: 'blob',
        })
        .then((res) => res.data)
        .then((blob) => {
          var file = new Blob([blob], { type: 'application/pdf' });
          var url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Misc-Report-${selectedDateStr}.pdf`);
          document.body.appendChild(link);
          link.click();
          link?.parentNode?.removeChild(link);
        })
        .catch((err) => {
          console.log(err);
          alert('Error while downloading Laundry report!');
        })
        .finally(() => setLoading(false));
    }
  };

  const generateRoomTransfersReport = () => {
    if (reportDate) {
      setLoading(true);

      const selectedDateStr = moment(reportDate).format('YYYY-MM-DD');

      axios
        .get(
          `/api/getConsolidatedReport?date=${selectedDateStr}&type=transfers`,
          {
            responseType: 'blob',
          }
        )
        .then((res) => res.data)
        .then((blob) => {
          var file = new Blob([blob], { type: 'application/pdf' });
          var url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `Room Transfers Report-${selectedDateStr}.pdf`
          );
          document.body.appendChild(link);
          link.click();
          link?.parentNode?.removeChild(link);
        })
        .catch((err) => {
          console.log(err);
          alert('Error while downloading Room Transfers report!');
        })
        .finally(() => setLoading(false));
    }
  };

  const generateBillDeductionsReport = () => {
    if (reportDate) {
      setLoading(true);

      const selectedDateStr = moment(reportDate).format('YYYY-MM-DD');

      axios
        .get(
          `/api/getConsolidatedReport?date=${selectedDateStr}&type=billDeductions`,
          {
            responseType: 'blob',
          }
        )
        .then((res) => res.data)
        .then((blob) => {
          var file = new Blob([blob], { type: 'application/pdf' });
          var url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `RS Paid Bills Report-${selectedDateStr}.pdf`
          );
          document.body.appendChild(link);
          link.click();
          link?.parentNode?.removeChild(link);
        })
        .catch((err) => {
          console.log(err);
          alert('Error while downloading RS Paid Bills report!');
        })
        .finally(() => setLoading(false));
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Reports</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <DatePicker
              className={classes.datePicker}
              selected={reportDate || null}
              onChange={(date) => {
                if (date != null && !Array.isArray(date)) {
                  setReportDate(date);
                }
              }}
              showTimeSelect={false}
              dateFormat="dd-MMM-yyyy"
              required
            />

            <PrimaryButton
              className={classes.button}
              onClick={() => generateCheckoutReport()}
            >
              Checkout
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => generateFoodReport()}
            >
              Food Bill
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => generateLaundryReport()}
            >
              Laundry Bill
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => generateRoomTransfersReport()}
            >
              Room Transfers
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => generateBillDeductionsReport()}
            >
              RS Paid Bills
            </PrimaryButton>
          </div>
          <hr className={classes.formSeparator} />
          <div className={classes.formContainer}>
            <PrimaryButton
              className={classes.button}
              onClick={() => navigate('/positionList', { replace: true })}
            >
              Position List
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => navigate('/advanceList', { replace: true })}
            >
              Advance List
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => navigate('/creditList', { replace: true })}
            >
              Credit List
            </PrimaryButton>
            <PrimaryButton
              className={classes.button}
              onClick={() => navigate('/roomAvailability', { replace: true })}
            >
              Room Details
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }
};

export default Reports;
